/**
 * ESTABLISHMENT-NOTIFICATIONS-PANEL
 * Notifications panel with establishment-specific computed notifications.
 */

import DocumentUpdateModal from "@/entities/document/ui/modal/DocumentUpdateModal"
import NotificationsPanel from "@/entities/notification/ui/NotificationsPanel"
import { useAppDispatch, useAppSelector } from "@/core/root-reducer"
import { useState } from "react"
import { useEstablishmentNotifications } from "../hooks/useEstablishmentNotifications"
import { Notification } from "@/entities/notification/model/notification.types"
import physicalRiskStore from "@/entities/physical-risk/model/physical-risk.store"
import { DocumentOutdatedNotificationType } from "@/entities/document/utils/document-outdated.utils"
import { PhysicalRisk } from "@/entities/physical-risk/model/physical-risk.types"
import preventionActionStore from "@/entities/prevention-action/model/prevention-action.store"
import { PreventionAction } from "@/entities/prevention-action/model/prevention-action.types"
import PreventionActionAddModal from "@/entities/prevention-action/ui/modal/PreventionActionAddModal"
import PhysicalRiskEditModal from "@/entities/physical-risk/ui/modal/PhysicalRiskEditModal"
import RpsSurveyImportModal from "@/entities/physical-risk/ui/modal/RpsSurveyImportModal"
import { WizardStep } from "@/entities/physical-risk/ui/wizard/physical-risk-wizard.constants"

export default function EstablishmentNotificationsPanel() {
  const dispatch = useAppDispatch()
  const { documentNotifications, notifications, preventionActionsWithoutDate, risksWithoutMeasure } =
    useEstablishmentNotifications()
  const preventionActions = useAppSelector(preventionActionStore.selectors.list)
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false)
  const [editingRisk, setEditingRisk] = useState<PhysicalRisk | null>(null)
  //L'etape ouverte depend de la tache cliquee : renseigner le risque, ou lui ajouter une mesure
  const [editingRiskStep, setEditingRiskStep] = useState<WizardStep>("DESCRIPTION")
  const [preventionActionIndex, setPreventionActionIndex] = useState<number | null>(null)
  const [isRpsSurveyModalOpen, setIsRpsSurveyModalOpen] = useState(false)
  const [preventionActionQueueIds, setPreventionActionQueueIds] = useState<string[]>([])
  const currentPreventionAction =
    preventionActionIndex !== null
      ? preventionActions.find((action) => action.id === preventionActionQueueIds[preventionActionIndex])
      : null

  function openRiskEditModal(risks: PhysicalRisk[], step: WizardStep) {
    if (!risks.length) return

    dispatch(physicalRiskStore.actions.select(risks.map((risk) => risk.id)))
    dispatch(physicalRiskStore.actions.setActive(risks[0]))
    setEditingRiskStep(step)
    setEditingRisk(risks[0])
  }

  function openPreventionActionModal(actions: PreventionAction[]) {
    if (!actions.length) return

    setPreventionActionQueueIds(actions.map((action) => action.id))
    dispatch(preventionActionStore.actions.setActive(actions[0]))
    setPreventionActionIndex(0)
  }

  function closePreventionActionModal() {
    setPreventionActionIndex(null)
    setPreventionActionQueueIds([])
    dispatch(preventionActionStore.actions.setActive(null))
  }

  function openNextPreventionAction() {
    if (preventionActionIndex === null) return

    const nextIndex = preventionActionIndex + 1
    const nextAction = preventionActions.find((action) => action.id === preventionActionQueueIds[nextIndex])

    if (!nextAction) {
      closePreventionActionModal()
      return
    }

    dispatch(preventionActionStore.actions.setActive(nextAction))
    setPreventionActionIndex(nextIndex)
  }

  function handleNotificationClick(notification: Notification) {
    if (
      notification.type === "document-needs-generation" ||
      documentNotifications.includes(notification.type as DocumentOutdatedNotificationType)
    ) {
      setIsUpdateModalOpen(true)
    } else if (notification.type === "measures-missing") {
      //La tache ouvre l'etape Prevention du risque concerne, la ou la mesure s'ajoute
      const risk = risksWithoutMeasure.find((item) => item.id === notification.itemId)
      openRiskEditModal(risk ? [risk] : risksWithoutMeasure, "PREVENTION")
    } else if (notification.type === "prevention-actions-without-date") {
      //Chaque carte ouvre uniquement sa propre mesure.
      const action = preventionActionsWithoutDate.find((item) => item.id === notification.itemId)
      openPreventionActionModal(action ? [action] : preventionActionsWithoutDate)
    } else if (notification.type === "rps-survey-to-link") {
      setIsRpsSurveyModalOpen(true)
    }
  }

  const computedNotifications: Notification[] = notifications.map((notification) => ({
    ...notification,
    onClick: () => handleNotificationClick(notification)
  }))
  return (
    <>
      <NotificationsPanel notifications={computedNotifications} />

      {isRpsSurveyModalOpen && <RpsSurveyImportModal onClose={() => setIsRpsSurveyModalOpen(false)} />}

      {editingRisk && (
        <PhysicalRiskEditModal initialStep={editingRiskStep} risk={editingRisk} onClose={() => setEditingRisk(null)} />
      )}

      {isUpdateModalOpen && (
        <DocumentUpdateModal onClose={() => setIsUpdateModalOpen(false)} />
      )}

      {currentPreventionAction && preventionActionIndex !== null && (
        <PreventionActionAddModal isEditing onClose={closePreventionActionModal} onSaved={openNextPreventionAction} />
      )}
    </>
  )
}
